import { Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { ArrowLeft, Users } from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import SettingsNav from '@/components/SettingsNav'
import type { Agent } from '@/types/shared'

interface WidgetAgent extends Agent {
  show_in_widget: boolean
}

interface WidgetProps {
  currentAgent: Agent
  agents: WidgetAgent[]
}

export default function Widget({ currentAgent, agents }: WidgetProps) {
  const [visibleIds, setVisibleIds] = useState<Set<number>>(
    new Set(agents.filter((a) => a.show_in_widget).map((a) => a.id))
  )
  const [saving, setSaving] = useState(false)

  const toggle = (id: number) => {
    setVisibleIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const save = () => {
    setSaving(true)
    router.patch('/settings/widget', {
      visible_agent_ids: Array.from(visibleIds),
    }, {
      onFinish: () => setSaving(false),
    })
  }

  const hasChanges = agents.some((a) =>
    a.show_in_widget !== visibleIds.has(a.id)
  )

  return (
    <>
      <Head title="Widget" />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="settings" />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center px-6">
            <Link
              href="/inbox"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mr-4"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="text-lg font-semibold text-gray-900">Configurações</h1>
          </header>

          <div className="flex-1 flex min-h-0">
            <SettingsNav activePage="widget" isAdmin={currentAgent.role === 'admin'} />

            <main className="flex-1 overflow-auto p-6">
              <div className="max-w-2xl">
                <section className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Users className="w-5 h-5 text-gray-700" />
                    <h2 className="text-lg font-semibold text-gray-900">Equipe Visível no Widget</h2>
                  </div>
                  <p className="text-sm text-gray-600 mb-6">
                    Selecione quais agentes aparecem no cabeçalho do widget para os visitantes.
                  </p>

                  <div className="divide-y divide-gray-100">
                    {agents.map((agent) => (
                      <div key={agent.id} className="flex items-center justify-between py-3">
                        <div className="flex items-center gap-3">
                          {agent.avatar_url ? (
                            <img
                              src={agent.avatar_url}
                              alt={agent.name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                              {agent.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                            <p className="text-xs text-gray-500">{agent.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => toggle(agent.id)}
                          className={`relative w-11 h-6 rounded-full transition-colors ${
                            visibleIds.has(agent.id) ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                              visibleIds.has(agent.id) ? 'translate-x-5' : ''
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <button
                      onClick={save}
                      disabled={saving || !hasChanges}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50"
                    >
                      {saving ? 'Salvando...' : 'Salvar'}
                    </button>
                  </div>
                </section>
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  )
}
